import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  SxProps,
  Stack,
  Paper,
} from '@mui/material';
import {
  SearchFieldClearIconButton,
  SearchFieldSearchIconGraphic,
  SEARCH_FIELD_ICON_SIZE,
} from 'Components/SearchFieldIcon';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

const SEARCH_ICON_INSET = `calc(16px + ${SEARCH_FIELD_ICON_SIZE}px + 8px)`;
const CLEAR_ICON_INSET = 'calc(16px + 20px + 8px)';

interface IOption {
  label: string;
  value: string;
  group?: string;
}

interface ICustomNewAutoComplete {
  value?: string;
  setValue: (value: string | null) => void;
  onClick?: Function;
  options: IOption[];
  recentOptions: IOption[];
  label: string;
  placeholder?: string;
  style?: SxProps;
  paperComponent?: React.ReactNode;
  groupName?: string;
  handleSubCategoryClick?: (id: number) => void;
  getRequestId?: (name: string) => number | undefined;
}

export const CustomNewAutoComplete: React.FC<ICustomNewAutoComplete> = ({
  value,
  setValue,
  onClick,
  options,
  recentOptions,
  label,
  placeholder,
  style,
  paperComponent,
  groupName,
}) => {
  const isDesktop = useIsDesktop();
  const [isOpen, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const autoC = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const clearSearchLabel = Dictionary[DICTIONARY.General].clearSearch;

  const allOptions: IOption[] = [
    ...recentOptions.map((option) => ({
      ...option,
      group: groupName,
    })),
    ...options.map((option) => ({
      ...option,
      group: '',
    })),
  ];

  const lastOption = allOptions[allOptions.length - 1];
  const fieldHeight = isDesktop ? '56px' : '42px';
  const hasInputValue = inputValue.length > 0 || Boolean(value);

  useEffect(() => {
    if (!value) {
      setInputValue('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !autoC.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (paperComponent) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [paperComponent]);

  const handleClear = () => {
    setValue(null);
    setInputValue('');
  };

  const customPaperSx = {
    width: 'calc(100% + 2px)',
    marginInline: '-1px',
    boxSizing: 'border-box',
    padding: '14px',
    boxShadow: 'none',
    bgcolor: '#fff',
    color: 'text.secondary',
    fontWeight: 400,
    borderRadius: '0 0 16px 16px',
    outline: '0px',
    border: '1px solid',
    borderColor: 'primary.main',
    borderTop: '0px',
    '& ul': {
      bgcolor: '#fff',
      position: 'relative',
      p: '0px',
    },
    '& .MuiAutocomplete-option': {
      display: 'block !important',
      width: '100%',
      textAlign: 'right',
      whiteSpace: 'normal',
      boxSizing: 'border-box',
    },
    '& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused': {
      borderRadius: '12px',
      border: '1px solid #5B86EF',
      bgcolor: 'primary.shade_100',
    },
  } as const;

  const CustomPaper: React.FC<React.ComponentProps<typeof Paper>> = ({
    sx,
    ...paperProps
  }) => <Paper {...paperProps} sx={{ ...customPaperSx, ...sx }} />;

  return (
    <Stack
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: isDesktop ? '450px' : '100%',
        m: isDesktop ? undefined : '0 auto',
        boxSizing: 'border-box',
        bgcolor: 'white',
        border: '1px solid',
        borderColor: isOpen ? 'primary.main' : 'primary.shade_200',
        borderRadius: isOpen ? '16px 16px 0 0' : '56px',
        borderBottom: isOpen ? 0 : undefined,
        overflow: 'visible',
        ...style,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: '16px',
          transform: 'translateY(-50%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <SearchFieldSearchIconGraphic />
      </Box>
      {hasInputValue && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '16px',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        >
          <SearchFieldClearIconButton
            ariaLabel={clearSearchLabel}
            onClear={handleClear}
          />
        </Box>
      )}
      <Autocomplete
        ref={autoC}
        disablePortal
        disableClearable
        open={isOpen}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        value={
          value
            ? allOptions.find((option) => option.value === value)
            : undefined
        }
        onChange={(_, newValue: IOption | null) =>
          setValue(newValue ? newValue.value : null)
        }
        options={allOptions}
        groupBy={(option) => option?.group || ''}
        getOptionLabel={(option) => option?.label || ''}
        renderInput={(params) => (
          <TextField
            onClick={() => {
              if (onClick) onClick();
            }}
            {...params}
            label={label}
            placeholder={placeholder || label}
            slotProps={{
              htmlInput: { ...params.inputProps, 'aria-label': label },
              inputLabel: {
                ...params.InputLabelProps,
                shrink: true,
                sx: { display: 'none' },
              },
            }}
          />
        )}
        noOptionsText={
          <Typography role='alert'>{dictionary.noOptions}</Typography>
        }
        renderGroup={(params) => (
          <Stack
            key={params.key}
            sx={{
              mb: '10px',
            }}
          >
            {params.group !== '' && (
              <Typography
                variant={isDesktop ? 'body2' : 'body5'}
                sx={{
                  fontWeight: 'bold',
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'start',
                }}
              >
                {params.group}
              </Typography>
            )}
            {params.children}
          </Stack>
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Typography
              key={key}
              component='li'
              {...optionProps}
              variant='body3'
              sx={{
                fontWeight: option === lastOption ? 'bold' : 'normal',
                py: '13px',
                px: '14px',
              }}
            >
              {option?.label || ''}
            </Typography>
          );
        }}
        popupIcon={null}
        slotProps={{
          popupIndicator: { sx: { display: 'none' } },
          popper: {
            sx: {
              width: '100%',
            },
          },
        }}
        sx={{
          width: '100%',
          bgcolor: 'transparent',
          '& .MuiAutocomplete-popper': {
            width: '100%',
          },
          borderRadius: isOpen ? '16px 16px 0 0' : '56px',
          '&&& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
            legend: {
              display: 'none',
              maxWidth: 0,
            },
          },
          '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
            border: 'none',
          },
          '&&& .MuiInputBase-root input[dropdown="closed"] ~ fieldset': {
            border: 'none',
          },
          '&&& .MuiOutlinedInput-root': {
            height: fieldHeight,
            px: 0,
            pr: SEARCH_ICON_INSET,
            pl: hasInputValue ? CLEAR_ICON_INSET : '16px',
          },
          '&&& .MuiOutlinedInput-input': {
            p: 0,
            textAlign: 'right',
            '&::placeholder': { opacity: 1, color: 'text.disabled' },
          },
        }}
        slots={{
          paper: CustomPaper,
        }}
      />
    </Stack>
  );
};

export default CustomNewAutoComplete;
